import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes, Icon, IconNames, SkillIcon } from 'atoms';
import { parseDateOnly } from 'services/DateService';
import type { IFeedSkill } from 'core/hooks/CareerCenter/transform/transformFeedVacancy';

/** Interaction state as it arrives on the wire: 'saved' | 'applied' | null. */
export type CareerVacancyInteraction = 'saved' | 'applied' | null;

export interface ICareerVacancyCard {
  title: string;
  companyName: string;
  location: string;
  /** Raw wire enum values — the card resolves labels via i18n and hides blanks. */
  workMode: string;
  seniority: string;
  employmentType: string;
  /** Pre-formatted salary range; empty string hides the row. */
  salary: string;
  description: string;
  directionTitles: string[];
  /** Навыки объектами, а не строками: чип должен УМЕТЬ стать фильтром, а для
   *  этого ему нужен id. matched — навык есть в профиле студента, slug и
   *  iconUrl — знак технологии на чипе. */
  skills: IFeedSkill[];
  /** Какие навыки уже выбраны фильтром — чип показывает это состоянием. */
  activeSkillIds: string[];
  onSkillClick: (skillId: string) => void;
  /** Сколько навыков вакансии совпало с профилем; 0 прячет строку. */
  matchedSkillCount: number;
  /** Серверный признак свежести (часы сервера, не браузера). */
  isNew: boolean;
  applyUrl: string;
  /** ISO date; empty hides the row. */
  expiresAt: string;
  /** ISO datetime the vacancy went live; empty hides the row and the badge. */
  publishedAt: string;
  interaction: CareerVacancyInteraction;
  onSave: () => void;
  /**
   * Можно ли сохранять. `false` — предпросмотр методиста (ТЗ 0039 доводка):
   * закладка ему негде и не за кем хранится, а показать кнопку значит соврать,
   * что она сработает. Смотреть саму вакансию при этом можно.
   */
  canSave?: boolean;
  /** Открывает подробный вид — описание целиком и все атрибуты. */
  onOpenDetails: () => void;
  actionLoading: boolean;
}

const MAX_SKILL_CHIPS = 8;

/**
 * Одна вакансия в ленте (ТЗ 0018 §B2, переделана после ручной проверки).
 *
 * Что изменилось и почему:
 *
 * * ГЛАВНОЕ действие — «Посмотреть вакансию» на внешнюю ссылку. Раньше это была
 *   мелкая текстовая ссылка в углу, а низ карточки занимали две одинаковые
 *   кнопки «Zapisz» и «Aplikuję», которые НИЧЕГО не отправляли работодателю:
 *   первая клала строку в избранное, вторая — самоотметку «я где-то откликнулся».
 *   Пользователю они читались как два способа откликнуться;
 * * кнопка «Aplikuję» убрана совсем (решение владельца), сохранение осталось
 *   иконкой-переключателем — теперь его можно снять;
 * * у каждого атрибута своя иконка. Карточки выглядели одинаково не только
 *   из-за оформления: обязательны лишь заголовок и ссылка, поэтому у большинства
 *   вакансий заполнены два поля из семи, а остальные блоки просто не рисуются.
 *   Иконка + подпись показывают, ЧТО именно заполнено;
 * * клик по карточке открывает подробный вид: описание здесь обрезано тремя
 *   строками, и до правки прочитать его целиком было негде.
 */
export const CareerVacancyCard = memo(
  ({
    title,
    companyName,
    location,
    workMode,
    seniority,
    employmentType,
    salary,
    description,
    directionTitles,
    skills,
    activeSkillIds,
    onSkillClick,
    matchedSkillCount,
    isNew,
    applyUrl,
    expiresAt,
    publishedAt,
    interaction,
    onSave,
    canSave = true,
    onOpenDetails,
    actionLoading,
  }: ICareerVacancyCard) => {
    const { t } = useTranslation();

    // Закладка залита ТОЛЬКО при 'saved'. `applied` — историческая самоотметка
    // «я где-то откликнулся» (кнопку убрали 27.07.2026), и сохранением она
    // никогда не была: фильтр «Только сохранённые» идёт по `kind='saved'`, а
    // кнопка шлёт `kind='saved'`. Пока `applied` считался сохранением, у таких
    // вакансий кнопка говорила «Удалить из сохранённых», в фильтр они не
    // попадали, а нажатие сохранение СОЗДАВАЛО.
    const isSaved = interaction === 'saved';

    const workModeLabel = workMode
      ? t(`careerProfile.workMode.${workMode}`, { defaultValue: workMode })
      : '';
    const seniorityLabel = seniority
      ? t(`careerProfile.seniority.${seniority}`, { defaultValue: seniority })
      : '';
    const employmentLabel = employmentType
      ? t(`careerCenter.vacancies.employment.${employmentType}`, { defaultValue: employmentType })
      : '';

    const skillChips = skills.slice(0, MAX_SKILL_CHIPS);
    const extraSkills = skills.length - skillChips.length;

    const facts = [
      { key: 'location', icon: IconNames.VacancyLocation, value: location },
      { key: 'workMode', icon: IconNames.VacancyMode, value: workModeLabel },
      { key: 'seniority', icon: IconNames.VacancyLevel, value: seniorityLabel },
      { key: 'employment', icon: IconNames.VacancyEmployment, value: employmentLabel },
      { key: 'salary', icon: IconNames.VacancySalary, value: salary },
      {
        key: 'published',
        icon: IconNames.Calendar,
        value: publishedAt
          ? `${t('careerCenter.vacancies.publishedAt')}: ${parseDateOnly(publishedAt)}`
          : '',
      },
      {
        key: 'expires',
        // Своя иконка: обе даты рисовались одним календарём, и «опубликована»
        // от «активна до» отличалась только текстом.
        icon: IconNames.Timer,
        value: expiresAt
          ? `${t('careerCenter.vacancies.expiresAt')}: ${parseDateOnly(expiresAt)}`
          : '',
      },
    ].filter((fact) => Boolean(fact.value));

    return (
      <Card>
        <CardHeader>
          <HeaderMain onClick={onOpenDetails} role="button" tabIndex={0}>
            <CardTitle>
              {title}
              {isNew ? <NewBadge>{t('careerCenter.vacancies.new')}</NewBadge> : null}
            </CardTitle>
            <CompanyLine>
              <Company>{companyName}</Company>
              {location ? <Location>· {location}</Location> : null}
            </CompanyLine>
          </HeaderMain>

          {canSave ? (
            <SaveToggle
              type="button"
              saved={isSaved}
              disabled={actionLoading}
              onClick={onSave}
              title={`${t(
                isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save',
              )}`}
              aria-pressed={isSaved}
              aria-label={`${t(
                isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save',
              )}`}>
              <Icon
                name={isSaved ? IconNames.VacancyBookmarkFilled : IconNames.VacancyBookmark}
                width="18px"
                height="18px"
              />
            </SaveToggle>
          ) : null}
        </CardHeader>

        {facts.length ? (
          <FactRow>
            {facts.map((fact) => (
              <Fact key={fact.key} accent={fact.key === 'salary'}>
                <Icon name={fact.icon} width="14px" height="14px" />
                {fact.value}
              </Fact>
            ))}
          </FactRow>
        ) : null}

        {description ? <Description>{description}</Description> : null}

        {directionTitles.length || skillChips.length ? (
          <TagRow>
            {directionTitles.map((direction) => (
              <DirectionTag key={`dir-${direction}`}>{direction}</DirectionTag>
            ))}
            {skillChips.map((skill) => (
              <SkillTag
                key={`skill-${skill.id}`}
                as="button"
                type="button"
                matched={skill.matched}
                active={activeSkillIds.includes(skill.id)}
                aria-pressed={activeSkillIds.includes(skill.id)}
                onClick={() => onSkillClick(skill.id)}>
                {/* Знак технологии: лента была единственным экраном, где навыки
                    оставались голым текстом, хотя на карте навыков и в
                    профессиях рядом стоят значки. */}
                <SkillIcon
                  slug={skill.slug}
                  name={skill.name}
                  category={skill.category}
                  iconUrl={skill.iconUrl}
                  size={16}
                />
                {skill.name}
              </SkillTag>
            ))}
            {extraSkills > 0 ? (
              <SkillTag>{t('careerCenter.vacancies.moreSkills', { n: extraSkills })}</SkillTag>
            ) : null}
          </TagRow>
        ) : null}

        {matchedSkillCount > 0 ? (
          <MatchLine>
            <MatchDot aria-hidden />
            {t('careerCenter.vacancies.matchedSkills', { n: matchedSkillCount })}
          </MatchLine>
        ) : null}

        <Actions>
          <DetailsButton variant="text" size={ButtonSizes.sm} onClick={onOpenDetails}>
            {t('careerCenter.vacancies.details')}
          </DetailsButton>

          <OpenLink href={applyUrl} target="_blank" rel="noopener noreferrer">
            {t('careerCenter.vacancies.open')}
          </OpenLink>
        </Actions>
      </Card>
    );
  },
);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background-color: ${Colors.white};

  ${tablet} {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderMain = styled.div`
  min-width: 0;
  cursor: pointer;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const NewBadge = styled.span`
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: 10px;
  background: ${Colors.main};
  /* white в тёмной теме = #141414, а в светлой #FFF на жёлтом даёт 1.2:1 —
     подпись «новая» не читалась. */
  color: ${Colors.btn_text};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  ${fontService.getFont({ size: 11, lineHeight: 145, weight: FontWeight.bold })}
`;

const CompanyLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
`;

const Company = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Location = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

/** ГЛАВНОЕ действие карточки: уводит на оригинал вакансии. */
const OpenLink = styled.a`
  flex-shrink: 0;
  padding: 10px 18px;
  border: 1px solid ${Colors.main};
  border-radius: 10px;
  background-color: ${Colors.main};
  /* Главная кнопка карточки: на жёлтом только btn_text. */
  color: ${Colors.btn_text};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    opacity: 0.9;
    color: ${Colors.btn_text};
  }
`;

const FactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

/** Иконка + значение. Иконки на `currentColor`, поэтому меняют цвет вместе с чипом. */
const Fact = styled.span<{ accent?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}

  /* Зарплата — единственный факт, который человек ищет глазами первым, поэтому
     акцент нужен. Но НЕ сплошной заливкой marker_green (#0E6245) с тёмным
     текстом поверх: тёмное по тёмному не читается, и владелец назвал этот цвет
     странным. Светлая подложка + тёмно-зелёная надпись полужирным.
     (Обратных кавычек в этом комментарии быть не может — он внутри шаблонной
     строки, и они оборвали бы литерал.) */
  ${({ accent }) =>
    accent &&
    css`
      background-color: rgba(14, 98, 69, 0.1);
      color: ${Colors.marker_green};
      font-weight: 700;
    `}
`;

/** Закладка: единственное оставшееся действие-состояние, и оно ОТМЕНЯЕМО. */
const SaveToggle = styled.button<{ saved?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid ${({ saved }) => (saved ? Colors.main : Colors.system_gray)};
  border-radius: 10px;
  background: none;
  cursor: pointer;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${({ saved }) => (saved ? Colors.marker_green : Colors.gray_dark)};

  &:hover:not(:disabled) {
    border-color: ${Colors.main};
    /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
    color: ${Colors.black};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Description = styled.p`
  margin: 16px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

/**
 * Направление — обычная метка, а не акцент.
 *
 * 🔴 Раньше рисовалось жёлтой рамкой и ЖЁЛТЫМ ТЕКСТОМ. Контраст #FFDF17 на
 * белом — около 1.4:1 при норме 4.5:1; владелец прочёл это как «жёлтым
 * непонятно, читается плохо». Жёлтый — заливка кнопки с чёрным текстом, а не
 * цвет надписи (правило записано у палитры, context/constants/colors.ts).
 */
const DirectionTag = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  background: ${Colors.bg_gray};
  color: ${Colors.black};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

/**
 * Чип навыка — теперь кнопка-фильтр.
 *
 * Три состояния, и они не одно и то же:
 * `matched`  — навык есть в профиле студента (почему вакансия наверху);
 * `active`   — по нему сейчас фильтруют;
 * обычный    — просто требование вакансии.
 * Акцент для `matched` берём тот же, что уже используется у направления, —
 * новый визуальный язык ради этого заводить незачем.
 */
const SkillTag = styled.span<{ matched?: boolean; active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid ${Colors.system_gray};
  color: ${Colors.black};
  background: transparent;
  cursor: pointer;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}

  /* «Этот навык у меня есть» — успех, значит зелёная рамка и обычный текст.
     Жёлтым текстом это читалось хуже, чем не читалось вовсе. */
  ${({ matched }) =>
    matched &&
    css`
      border-color: ${Colors.signal_green};
    `}

  /* Фильтр включён — жёлтая заливка и btn_text поверх. НЕ black и не
     white: оба переворачиваются с темой, жёлтый — нет, и подпись пропадает
     в одной из тем (правило в colors.ts). */
  ${({ active }) =>
    active &&
    css`
      border-color: ${Colors.main};
      background: ${Colors.main};
      color: ${Colors.btn_text};
      font-weight: 500;
    `}
`;

const MatchLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  color: ${Colors.black};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const MatchDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${Colors.signal_green};
  flex: none;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const DetailsButton = styled(Button)`
  width: auto;
`;
